import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import Chip from "@/components/ui/chip";
import { Calendar, Close, Download, Tickets, Sent } from "@/components/icons";
import type {
  SupportTicketDetailResponse,
  SupportTicketFile,
  SupportTicketThreadMessage,
  TicketStatusBackend,
} from "@/features/support-tickets";
import {
  SupportTicketStatus,
  SUPPORT_TICKET_STATUS_CHIP_TYPE,
  SUPPORT_TICKET_STATUS_LABELS_TR,
} from "@/features/support-tickets";
import { getFileDownloadUrl } from "@/lib/files.api";
import { clientSupportTicketsApi } from "../../services/client-support-tickets.api";
import styles from "./ClientSupportTicketDetailModal.module.scss";

interface ClientSupportTicketDetailModalProps {
  ticketId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

function formatDateTime(dateIso: string): string {
  const d = new Date(dateIso);
  if (Number.isNaN(d.getTime())) return dateIso;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

function getFileExtension(file: SupportTicketFile): string {
  if (file.mimeType) {
    const [type, subtype] = file.mimeType.split("/");
    if (type && subtype) return subtype.toUpperCase();
  }
  const name = file.originalName ?? "";
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex !== -1 && dotIndex < name.length - 1) {
    return name.slice(dotIndex + 1).toUpperCase();
  }
  return "DOSYA";
}

function getShortFileName(file: SupportTicketFile): string {
  const name = file.originalName ?? "";
  if (name.length <= 40) return name;
  return `${name.slice(0, 37)}...`;
}

const ClientSupportTicketDetailModal: React.FC<
  ClientSupportTicketDetailModalProps
> = ({ ticketId, isOpen, onClose }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<SupportTicketDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null,
  );
  const [replyInput, setReplyInput] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen || !ticketId) {
      setData(null);
      setReplyInput("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    clientSupportTicketsApi
      .getDetail(ticketId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, ticketId]);

  if (!isOpen || !ticketId) return null;

  const status: TicketStatusBackend = data?.status ?? 2;
  const isResolved = status === SupportTicketStatus.RESOLVED;

  const refetch = async () => {
    if (!ticketId) return;
    try {
      const res = await clientSupportTicketsApi.getDetail(ticketId);
      setData(res);
    } catch {
      // sessiz: mevcut veriyi koru
    }
  };

  const handleSend = async () => {
    const content = replyInput.trim();
    if (!ticketId || !data || isResolved || !content) return;
    setSending(true);
    try {
      await clientSupportTicketsApi.postMessage(ticketId, { content });
      setReplyInput("");
      await refetch();
    } catch (err) {
      toast.error(
        (err as Error)?.message ?? t("clientSupportTickets.detail.sendError"),
      );
    } finally {
      setSending(false);
    }
  };

  const handleDownload = async (file: SupportTicketFile) => {
    try {
      setDownloadingFileId(file.id);
      const url = await getFileDownloadUrl(file.id);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = file.originalName || "dosya";
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Dosya indirilemedi:", error);
    } finally {
      setDownloadingFileId((current) => (current === file.id ? null : current));
    }
  };

  const customHeader = (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <span className={styles.headerIcon}>
          <Tickets width={20} height={20} color="#E7212B" />
        </span>
        <h3 className={styles.headerTitle}>
          {t("clientSupportTickets.detail.title")}
        </h3>
      </div>
      <button
        type="button"
        className={styles.headerCloseBtn}
        onClick={onClose}
        aria-label={t("common.close")}
      >
        <Close width={20} height={20} color="#505766" />
      </button>
    </div>
  );

  const renderBubbleFile = (file: SupportTicketFile) => (
    <div className={styles.bubbleFile}>
      <div className={styles.attachmentBadge}>{getFileExtension(file)}</div>
      <span className={styles.bubbleFileName}>{getShortFileName(file)}</span>
      <button
        type="button"
        className={styles.downloadButton}
        onClick={() => handleDownload(file)}
        disabled={downloadingFileId === file.id}
        aria-label={t("common.download")}
      >
        <Download width={18} height={18} />
      </button>
    </div>
  );

  const renderMessage = (m: SupportTicketThreadMessage) => {
    // Client portalında: kendi mesajım (isStaff=false) sağda, destek (isStaff=true) solda.
    const isOwn = !m.isStaff;
    return (
      <div
        key={m.id}
        className={`${styles.msgRow} ${isOwn ? styles.msgRowOwn : styles.msgRowStaff}`}
      >
        <div
          className={`${styles.bubble} ${isOwn ? styles.bubbleOwn : styles.bubbleStaff}`}
        >
          <div className={styles.bubbleMeta}>
            {(isOwn
              ? t("clientSupportTickets.detail.youLabel")
              : m.senderName || t("clientSupportTickets.detail.staffLabel")) +
              " · " +
              formatDateTime(m.createdAt)}
          </div>
          {m.content && <div className={styles.bubbleContent}>{m.content}</div>}
          {m.file && renderBubbleFile(m.file)}
        </div>
      </div>
    );
  };

  const visibleMessages = (data?.messages ?? []).filter(
    (m) => m.content || m.file,
  );

  const bodyContent = loading ? (
    <div className={styles.loading}>{t("clientSupportTickets.loading")}</div>
  ) : !data ? (
    <div className={styles.loading}>
      {t("clientSupportTickets.detail.notFound")}
    </div>
  ) : (
    <div className={styles.body}>
      <div className={styles.metaRow}>
        <div className={styles.metaLeft}>
          <Calendar width={18} height={18} />
          <div className={styles.metaText}>
            <span className={styles.metaDate}>
              {formatDateTime(data.createdAt)}
            </span>
          </div>
        </div>
        <Chip
          type={SUPPORT_TICKET_STATUS_CHIP_TYPE[status]}
          size="small"
          variant="filled"
        >
          {SUPPORT_TICKET_STATUS_LABELS_TR[status]}
        </Chip>
      </div>

      <div className={styles.title}>{data.title}</div>

      {/* Karşılıklı konuşma */}
      <div className={styles.sectionTitle}>
        {t("clientSupportTickets.detail.conversation")}
      </div>
      <div className={styles.thread}>
        {visibleMessages.length === 0 && (
          <div className={styles.messageBubble}>
            <div className={styles.description}>{data.description}</div>
          </div>
        )}
        {visibleMessages.map(renderMessage)}
      </div>

      {isResolved ? (
        <div className={styles.lockedNote}>
          {t("clientSupportTickets.detail.locked")}
        </div>
      ) : (
        <div className={styles.replyForm}>
          <div className={styles.replyInputWrap}>
            <Input
              variant="outlined"
              placeholder={t("clientSupportTickets.detail.replyPlaceholder")}
              value={replyInput}
              maxLength={10000}
              onChange={(e) => setReplyInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={sending}
            />
          </div>
          <button
            type="button"
            className={styles.sendButton}
            onClick={handleSend}
            disabled={sending || !replyInput.trim()}
            aria-label={t("common.send")}
          >
            <Sent width={20} height={20} color="#fff" />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      customHeader={customHeader}
      customFooter={null}
      size="lg"
    >
      {bodyContent}
    </Modal>
  );
};

export default ClientSupportTicketDetailModal;
